import React from "react";
import {
  COULEUR,
  LARGEUR,
  Figure,
  PhraseAbsence,
  estLibelle,
  estNombre,
  formatNombre,
} from "./_commun";

// Classement — des choses ordonnées, avec L'ÉCART NOMMÉ.
//
// Ce qu'elle apprend (registre) : « lequel décroche, et DE COMBIEN — l'écart
// est nommé, pas à mesurer à l'œil ». L'écran Argent liste les logements sans
// les ordonner ni chiffrer l'écart. Ici : barres ordonnées, celui qui décroche
// en terracotta, et « 23 % sous les autres » ÉCRIT à côté de sa barre.
//
// ══ FORME DES DONNÉES ═══════════════════════════════════════════════════════
//
//   {
//     unite?: "%" | "€" | string,
//     sens?:  "haut_mieux" (défaut) | "bas_mieux",   // pour des charges : plus bas = mieux
//     items:  [ { label: "lc2", v: 34 }, … ]           // ≥ 2 items, v fini
//   }
//
// L'écart est RELATIF à la moyenne des autres : (moyenne − v) / moyenne.
// Il n'est calculable que si cette moyenne est strictement positive ; sinon
// la phrase le dit au lieu d'afficher un pourcentage absurde.

const LIGNE = 26;
const MARGE = { gauche: 64, droite: 118, haut: 8, bas: 8 };

function validerClassement(d) {
  if (
    !d ||
    typeof d !== "object" ||
    !Array.isArray(d.items) ||
    d.items.length === 0
  )
    return { erreur: "pas de données sur cette période" };
  if (d.items.length < 2) return { erreur: "un seul élément — rien à classer" };
  if (
    !d.items.every(
      (it) =>
        it && typeof it === "object" && estLibelle(it.label) && estNombre(it.v),
    )
  )
    return { erreur: "données incomplètes — un élément n'a pas de valeur" };
  const sens = d.sens === "bas_mieux" ? "bas_mieux" : "haut_mieux";
  return { items: d.items, sens, unite: d.unite };
}

/** Calcule l'écart nommé de celui qui décroche, ou null s'il n'est pas calculable. */
export function ecartNomme(itemsOrdonnes, sens) {
  const dernier = itemsOrdonnes[itemsOrdonnes.length - 1];
  const autres = itemsOrdonnes.slice(0, -1);
  const moyenne = autres.reduce((s, it) => s + it.v, 0) / autres.length;
  if (!(moyenne > 0)) return { dernier, moyenne, pct: null };
  const brut =
    sens === "bas_mieux"
      ? (dernier.v - moyenne) / moyenne
      : (moyenne - dernier.v) / moyenne;
  return { dernier, moyenne, pct: Math.round(brut * 100) };
}

export default function Classement({ titre, donnees }) {
  const v = validerClassement(donnees);
  if (v.erreur) return <PhraseAbsence titre={titre} texte={v.erreur} />;
  const { items, sens, unite } = v;

  // Du meilleur au pire : celui qui décroche est TOUJOURS en bas.
  const ordonnes = [...items].sort((a, b) =>
    sens === "bas_mieux" ? a.v - b.v : b.v - a.v,
  );
  const { dernier, moyenne, pct } = ecartNomme(ordonnes, sens);

  const H = MARGE.haut + LIGNE * ordonnes.length + MARGE.bas;
  const largeurUtile = LARGEUR - MARGE.gauche - MARGE.droite;
  const maxAbs = Math.max(...ordonnes.map((it) => Math.abs(it.v)), 0);
  const largeur = (val) =>
    maxAbs > 0 ? (Math.abs(val) / maxAbs) * largeurUtile : 0;

  const mot = sens === "bas_mieux" ? "au-dessus des" : "sous les";
  let annotation;
  let conclusion;
  if (pct === null) {
    annotation = "écart non calculable";
    conclusion = `${titre} : ${dernier.label} est dernier (${formatNombre(dernier.v, unite)}), écart non calculable — la moyenne des autres n'est pas positive`;
  } else if (pct <= 0) {
    annotation = "dans la moyenne";
    conclusion = `${titre} : ${dernier.label} est dernier (${formatNombre(dernier.v, unite)}) mais dans la moyenne des autres (${formatNombre(moyenne, unite)})`;
  } else {
    annotation = `${pct} % ${mot} autres`;
    conclusion = `${titre} : ${dernier.label} décroche, ${pct} % ${mot} autres (${formatNombre(dernier.v, unite)} contre ${formatNombre(moyenne, unite)} en moyenne)`;
  }

  return (
    <Figure
      forme="classement"
      titre={titre}
      conclusion={conclusion}
      hauteur={H}
    >
      {ordonnes.map((it, i) => {
        const yc = MARGE.haut + centreLigne(i);
        const estDernier = i === ordonnes.length - 1;
        const couleur = estDernier ? COULEUR.terra : COULEUR.navy;
        const w = largeur(it.v);
        return (
          <g
            key={`${it.label}-${i}`}
            data-testid={estDernier ? "classement-decroche" : "classement-item"}
          >
            <text
              x={MARGE.gauche - 8}
              y={yc + 4}
              textAnchor="end"
              fontSize="11"
              fill={COULEUR.navyD}
              fontWeight={estDernier ? "600" : "400"}
            >
              {it.label}
            </text>
            <rect
              x={MARGE.gauche}
              y={yc - 8}
              width={Math.max(w, 1)}
              height={16}
              fill={couleur}
              rx="2"
            />
            <text
              x={MARGE.gauche + w + 6}
              y={yc + 4}
              fontSize="10.5"
              fill={COULEUR.navyD}
            >
              {formatNombre(it.v, unite)}
            </text>
          </g>
        );
      })}

      {/* La moyenne des autres, en repère, et l'écart ÉCRIT en toutes lettres. */}
      {maxAbs > 0 && moyenne >= 0 ? (
        <line
          x1={MARGE.gauche + largeur(moyenne)}
          x2={MARGE.gauche + largeur(moyenne)}
          y1={MARGE.haut}
          y2={H - MARGE.bas}
          stroke={COULEUR.grisD}
          strokeDasharray="3 3"
        />
      ) : null}
      <text
        data-testid="classement-ecart"
        x={LARGEUR - 4}
        y={MARGE.haut + centreLigne(ordonnes.length - 1) + 4}
        textAnchor="end"
        fontSize="11"
        fontWeight="600"
        fill={COULEUR.terra}
      >
        {annotation}
      </text>
    </Figure>
  );
}

function centreLigne(i) {
  return LIGNE * i + LIGNE / 2;
}
